import React, { useEffect, useState } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { LazyPage, Loading } from 'components/base';
import NotFound from 'pages/NotFound';
import App from 'layouts';
import AccountLayout from 'layouts/AccountLayout';
import * as routes from 'constants/routes';
import { auth } from 'config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { AUTH_SUCCESS, AUTH_LOGOUT } from 'store/auth/types';

const PrivateRoute = ({ children, authChecking }) => {
  const { user } = useSelector((state) => state.auth || {});
  if (authChecking) {
    return <Loading />;
  }
  return user ? children : <Navigate to={routes.LOGIN} />;
};

function Router() {
  const dispatch = useDispatch();
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        dispatch({
          type: AUTH_SUCCESS,
          payload: {
            uid: user.uid,
            email: user.email,
            name: user.displayName || 'Customer'
          }
        });
      } else {
        dispatch({ type: AUTH_LOGOUT });
      }
      setAuthChecking(false);
    });
    return () => unsubscribe();
  }, [dispatch]);

  return (
    <Routes>
      <Route path={routes.HOME} element={<App />}>
        <Route index element={<LazyPage page='Index/' />} />

        <Route path={routes.ABOUT} element={<LazyPage page='About' />} />
        <Route
          path={routes.CONTACTUS}
          element={<LazyPage page='ContactUs' />}
        />
        <Route path={routes.PRODUCTS} element={<LazyPage page='Products' />} />
        
        {/* Protected Routes */}
        <Route
          path={routes.PRODUCT}
          element={<LazyPage page='Products/Detail' />}
        />
        <Route 
          path={routes.CHECKOUT} 
          element={
            <PrivateRoute authChecking={authChecking}>
              <LazyPage page='Checkout' />
            </PrivateRoute>
          } 
        />

        {/* Auth Routes */}
        <Route path={routes.LOGIN} element={<LazyPage page='Login' />} />
        <Route path={routes.REGISTER} element={<LazyPage page='Register' />} />

        {/* Account Routes – wrapped in AccountLayout with sidebar (also protected) */}
        <Route 
          path="/account" 
          element={
            <PrivateRoute authChecking={authChecking}>
              <AccountLayout />
            </PrivateRoute>
          }
        >
          <Route path="dashboard" element={<LazyPage page='Account/Dashboard' />} />
          <Route path="profile" element={<LazyPage page='Account/Profile' />} />
          <Route path="addresses" element={<LazyPage page='Account/Addresses' />} />
          <Route path="orders" element={<LazyPage page='Account/Orders' />} />
          <Route path="tracking" element={<LazyPage page='Account/Tracking' />} />
          <Route path="wishlist" element={<LazyPage page='Account/Wishlist' />} />
          <Route path="payments" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="subscriptions" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="reviews" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="returns" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="downloads" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="loyalty" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="communication" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="security" element={<LazyPage page='Account/Security' />} />
          <Route path="preferences" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="recently-viewed" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="recommendations" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="business" element={<LazyPage page='Account/Placeholder' />} />
          <Route path="additional" element={<LazyPage page='Account/Placeholder' />} />
        </Route>
      </Route>

      <Route path='*' element={<NotFound />} />
    </Routes>
  );
}

export default Router;
